import {useEffect, useRef} from 'react';
import type {ReactNode} from 'react';
import classNames from 'classnames';
import {useTooltip} from '@react-aria/tooltip';

import {useOverlayPosition, OverlayContainer} from '@react-aria/overlays';

import type {AriaPositionProps} from '@react-aria/overlays';
import type {TooltipTriggerState} from '@react-stately/tooltip';

import isString from '@tinkoff/utils/is/string';
import {mergeProps} from '@react-aria/utils';

import {TooltipTextPrimary} from './tooltip-text-primary';
import styles from './styles.module.css';

const ANCHOR_SIZE = 5;
const ANCHOR_BASE_OFFSET = {'--anchor-base-offset': `-${ANCHOR_SIZE}px`} as React.CSSProperties;

export type Props = Omit<AriaPositionProps, 'overlayRef'> & {
    children: ReactNode;
    state: TooltipTriggerState;
    className?: string;
};

export const Tooltip = ({state, children, className, targetRef, placement: placementProp = 'top', ...props}: Props) => {
    const {tooltipProps} = useTooltip(props, state);
    const overlayRef = useRef<HTMLDivElement>(null);

    const {placement, overlayProps} = useOverlayPosition({
        overlayRef,
        placement: placementProp,
        targetRef,
        ...props,
        offset: (props.offset ?? 0) + ANCHOR_SIZE,
    });

    const content = isString(children) ? <TooltipTextPrimary>{children}</TooltipTextPrimary> : children;

    useEffect(() => {
        const targetNode = targetRef.current;
        const overlayNode = overlayRef.current;

        if (!targetNode || !overlayNode) {
            return;
        }

        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                // Исправляет проблему в хуке useOverlayPosition, из-за которой тултип прыгает
                // в верхний левый угол экрана в момент, когда targetNode устанавливается display: none.
                // https://jira.tcsbank.ru/browse/TIME-13633
                //
                // Сразу прячем оверлей как только targetNode стал невидим (например, display: none)
                if (entry.contentRect.width === 0 && entry.contentRect.height === 0) {
                    overlayNode.style.visibility = 'hidden';
                } else if (overlayNode.style.visibility === 'hidden') {
                    overlayNode.style.visibility = 'visible';
                }
            }
        });

        observer.observe(targetNode);

        return () => observer.disconnect();
    }, [targetRef]);

    const targetNodeRect = targetRef.current?.getBoundingClientRect();
    const overlayNodeRect = overlayRef.current?.getBoundingClientRect();

    const getAnchorPositionToken = () => {
        if (!targetNodeRect || !overlayNodeRect) {
            return {};
        }

        if (placement === 'top' || placement === 'bottom') {
            const overlayMiddle = overlayNodeRect.left + (overlayNodeRect.width / 2);
            const targetMiddle = targetNodeRect.left + (targetNodeRect.width / 2);

            return {
                '--anchor-position-left': `${targetMiddle - overlayMiddle}px`,
            };
        }

        const overlayMiddle = overlayNodeRect.top + (overlayNodeRect.height / 2);
        const targetMiddle = targetNodeRect.top + (targetNodeRect.height / 2);

        return {
            '--anchor-position-top': `${targetMiddle - overlayMiddle}px`,
        };
    };

    return (
        <OverlayContainer>
            <div
                ref={overlayRef}
                role='tooltip'
                className={classNames(styles.root, className)}
                {...mergeProps(tooltipProps, overlayProps)}
            >
                <div
                    className={styles.content}
                    data-placement={placement}
                    style={{
                        ...ANCHOR_BASE_OFFSET,
                        ...getAnchorPositionToken(),
                    }}
                >
                    {content}
                </div>
            </div>
        </OverlayContainer>
    );
};
